import {
  DEFAULT_PLACE_COUNT,
  formatPlaceRange,
  HostState,
  SHOW_PLACE_COUNTS,
  ShowPlaceCount,
} from '@schemas'
import { HostStateSelect } from '@/components/host-state-select'
import { useQueryClient } from '@tanstack/react-query'
import { SeriesField } from '@/components/shows/series-field'
import { ApiError } from '@schemas'
import { apiClient } from '@/lib/apiClient'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useBlocker, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { useForm, useWatch, useFormContext, type FieldPathByValue } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Label } from '@/components/ui/label'
import {
  ShowStaffRoster,
  ALL_STAFF_ROLES,
  type StaffRole,
  type StaffInput,
} from '@/components/shows/ShowStaffRoster'
import { DerivedPreviousEdition } from '@/components/shows/derived-previous-edition'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { STATUS_LABELS } from '@/lib/show-status'
import { SPECIAL_AWARD_LABELS, SPECIAL_AWARD_KEYS } from '@/data/special-awards'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormDescription,
  FormMessage,
} from '@/components/ui/form'
import { Skeleton } from '@/components/ui/skeleton'
import { useCurrentShow } from '@/context/current-show'
import {
  useCurrentUser,
  useUpdateShow,
  useDivisions,
  useAddShowStaff,
  useRemoveShowStaff,
  useOrganizations,
} from '@/api/hooks'
import type { TShowRoleAssignment, TShow } from '@schemas'
import { formatRoleList, useActorPermissions } from '@/hooks/useActorPermissions'
import { InviteUserDialog } from '@/pages/admin/InviteUserDialog'
import type { PickedPerson } from '@/pages/admin/account-components'
import { PageHeading } from '@/components/page-heading'
import {
  normalizeAwardsEligibility,
  extractBestInDivisionEnabled,
} from '@/data/special-awards-policy'
import { ShowSlug } from '@schemas'
import { SLUG_MAX_LENGTH, slugify } from '@/lib/slug'
import { normalizeDateInputValue, toApiDateOnly } from '@/lib/date'

function resolveApplyTiebreakers(pointsConfig: string | null | undefined): boolean {
  if (!pointsConfig || pointsConfig.trim().length === 0) return true
  try {
    const parsed = JSON.parse(pointsConfig)
    return typeof parsed?.applyTiebreakers === 'boolean' ? parsed.applyTiebreakers : true
  } catch {
    return true
  }
}

const showDetailsSchema = z.object({
  name: z.string().min(1, 'Show name is required'),
  slug: ShowSlug,
  orgId: z.guid({ message: 'Select an organization' }),
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().min(1, 'End date is required'),
  seriesId: z.string().nullable().optional(),
  location: z.string().optional(),
  hostState: HostState.nullable(),
  basicDetailsPublished: z.boolean(),
  rulesVersion: z.string().optional(),
  applyTiebreakers: z.boolean(),
  enableClubs: z.boolean(),
  labelOnlyMode: z.boolean(),
  labelPrintingDisabled: z.boolean(),
  staffMayExhibit: z.boolean(),
  reserveGrandEnabled: z.boolean(),
  defaultPlaceCount: ShowPlaceCount,
  bestInDivisionEnabled: z.boolean(),
  heroAwardEnabled: z.boolean(),
  clubSpiritAwardEnabled: z.boolean(),
  clubChampionAwardEnabled: z.boolean(),
  staffAwardEligibility: z.object({
    best_in_show: z.boolean(),
    grand_champion: z.boolean(),
    reserve_grand: z.boolean(),
  }),
})

type ShowDetailsFormValues = z.infer<typeof showDetailsSchema>
function formatDateInput(value?: string | null) {
  return normalizeDateInputValue(value)
}

function toDateOnly(value: string) {
  return toApiDateOnly(value)
}

function computeValidationState(
  assignments: TShowRoleAssignment[] | undefined,
  divisions?: Array<{ classes?: Array<{ id?: string | null }> | null }> | null
) {
  const totalClasses =
    divisions?.reduce(
      (count, division) => count + (Array.isArray(division.classes) ? division.classes.length : 0),
      0
    ) ?? 0
  const hasClasses = totalClasses > 0

  const counts = ALL_STAFF_ROLES.reduce<Record<StaffRole, number>>(
    (acc, role) => {
      acc[role] = assignments?.filter(assignment => assignment.role === role).length ?? 0
      return acc
    },
    {} as Record<StaffRole, number>
  )

  const hasJudges = counts.judge > 0
  const hasLeadSecretaries = counts.lead_secretary > 0
  const hasSecretaries = hasLeadSecretaries || counts.secretary > 0

  return {
    totalClasses,
    hasClasses,
    counts,
    hasJudges,
    hasLeadSecretaries,
    hasSecretaries,
  }
}

const SETUP_SECTIONS = [
  { value: 'details', label: 'Show details' },
  { value: 'participation', label: 'Participation' },
  { value: 'awards', label: 'Awards & judging' },
  { value: 'classes', label: 'Classes' },
  { value: 'staff', label: 'Staff' },
]

function getShowSettings(show: TShow | null): ShowDetailsFormValues {
  return {
    name: show?.name ?? '',
    slug: show?.slug ?? '',
    orgId: show?.orgId ?? '',
    startDate: formatDateInput(show?.startDate),
    endDate: formatDateInput(show?.endDate),
    seriesId: show?.seriesMembership?.series?.id ?? (show?.seriesMembership ? null : undefined),
    location: show?.location ?? '',
    hostState: show?.hostState ?? null,
    basicDetailsPublished: show?.basicDetailsPublished ?? false,
    rulesVersion: show?.rulesVersion ?? '',
    applyTiebreakers: resolveApplyTiebreakers(show?.pointsConfig),
    enableClubs: show?.enableClubs ?? true,
    labelOnlyMode: show?.labelOnlyMode ?? false,
    labelPrintingDisabled: show?.labelPrintingDisabled ?? false,
    staffMayExhibit: show?.staffMayExhibit ?? true,
    reserveGrandEnabled: show?.reserveGrandEnabled ?? false,
    defaultPlaceCount: show?.defaultPlaceCount ?? DEFAULT_PLACE_COUNT,
    heroAwardEnabled: show?.awardsPolicy?.heroAwardEnabled ?? false,
    bestInDivisionEnabled: extractBestInDivisionEnabled(show?.awardsPolicy),
    clubSpiritAwardEnabled: show?.awardsPolicy?.clubSpiritAwardEnabled ?? false,
    clubChampionAwardEnabled: show?.awardsPolicy?.clubChampionAwardEnabled ?? false,
    staffAwardEligibility: normalizeAwardsEligibility(show?.awardsPolicy),
  }
}

function SectionHeading({ title, description }: { title: string; description: string }) {
  return (
    <div className="space-y-1">
      <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  )
}

function SettingCheckbox({
  name,
  label,
  description,
  inverted = false,
  ariaLabel,
}: {
  name: FieldPathByValue<ShowDetailsFormValues, boolean>
  label: string
  description?: string
  inverted?: boolean
  ariaLabel?: string
}) {
  const { control } = useFormContext<ShowDetailsFormValues>()
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex items-start gap-3 space-y-0">
          <FormControl>
            <input
              type="checkbox"
              name={field.name}
              ref={field.ref}
              onBlur={field.onBlur}
              aria-label={ariaLabel}
              checked={inverted ? !field.value : field.value}
              onChange={event =>
                field.onChange(inverted ? !event.target.checked : event.target.checked)
              }
              className="mt-1 h-4 w-4 shrink-0 rounded border-input accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            />
          </FormControl>
          <div className="space-y-1">
            <FormLabel className="cursor-pointer leading-6">{label}</FormLabel>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </div>
        </FormItem>
      )}
    />
  )
}

export default function SetupPage() {
  const { currentShow, setCurrentShow } = useCurrentShow()
  const showId = currentShow?.id
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const requestedSection = searchParams.get('section')
  const section = SETUP_SECTIONS.some(item => item.value === requestedSection)
    ? requestedSection!
    : 'details'
  // A save can finish after the section changed, so its callbacks must not act on the URL they
  // were created with.
  const currentUrl = useRef({ section, searchParams, hash: location.hash })
  currentUrl.current = { section, searchParams, hash: location.hash }
  const setSection = (value: string) => {
    const current = currentUrl.current
    if (value === current.section) return
    const next = new URLSearchParams(current.searchParams)
    next.set('section', value)
    navigate({ search: `?${next}`, hash: current.hash }, { preventScrollReset: true })
  }
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const hasCustomSlug = Boolean(currentShow?.slug && currentShow.slug !== slugify(currentShow.name))
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false)

  const permissions = useActorPermissions(showId)
  const user = useCurrentUser()
  // Administrators can invite a staff person who cannot sign in yet.
  const [inviting, setInviting] = useState<PickedPerson | null>(null)
  const permissionLoading = permissions.staffQuery?.isLoading ?? false
  const canManageSettings = permissions.can('show:write')
  const canManageStaff = permissions.can('staff:write')
  const staffRolesLabel = useMemo(
    () => formatRoleList(permissions.getAllowedRoles('staff:write')),
    [permissions]
  )
  const staffPermissionNotice = permissionLoading
    ? 'Loading permissions…'
    : !canManageStaff
      ? `Managing staff requires ${staffRolesLabel}.`
      : undefined

  const canReadSeries = permissions.can('reports:results')
  const [seriesValidity, setSeriesValidity] = useState({ showId, valid: true })
  const setSeriesValid = useCallback(
    (valid: boolean) => setSeriesValidity({ showId, valid }),
    [showId]
  )
  // A read-only series cannot change and is never sent, so it must not block other settings.
  const seriesValid =
    !canReadSeries ||
    !currentShow?.seriesMembership ||
    !currentShow.seriesMembership.canEdit ||
    seriesValidity.showId !== showId ||
    seriesValidity.valid
  const queryClient = useQueryClient()
  const [seriesConflict, setSeriesConflict] = useState<TShow | null>(null)
  const seriesRevision = useRef(currentShow?.seriesMembership?.revision)
  const updateShow = useUpdateShow()
  const divisionsQuery = useDivisions(showId)
  const staffQuery = permissions.staffQuery
  const addStaff = useAddShowStaff()
  const removeStaff = useRemoveShowStaff()
  const organizationsQuery = useOrganizations()
  const organizationOptions = useMemo(
    () =>
      (organizationsQuery.data ?? [])
        .filter(org => typeof org.id === 'string')
        .map(org => ({
          id: org.id as string,
          name: org.name,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [organizationsQuery.data]
  )
  const organizationPlaceholder = organizationsQuery.isLoading
    ? 'Loading organizations…'
    : organizationOptions.length > 0
      ? 'Select organization'
      : 'No organizations available'

  const showForm = useForm<ShowDetailsFormValues>({
    resolver: zodResolver(showDetailsSchema),
    defaultValues: getShowSettings(currentShow),
    shouldFocusError: false,
  })

  const loadedShowFormId = useRef<string | undefined>(undefined)
  const dirtyShowFields = useRef(showForm.formState.dirtyFields)
  dirtyShowFields.current = showForm.formState.dirtyFields

  useEffect(() => {
    if (!currentShow) return
    const sameShow = loadedShowFormId.current === currentShow.id
    const keepDirtyValues = sameShow && Object.keys(dirtyShowFields.current).length > 0
    if (!sameShow || !dirtyShowFields.current.seriesId)
      seriesRevision.current = currentShow.seriesMembership?.revision
    if (!sameShow) setSeriesConflict(null)
    loadedShowFormId.current = currentShow.id
    showForm.reset(getShowSettings(currentShow), { keepDirtyValues })
    if (!keepDirtyValues || (!dirtyShowFields.current.slug && !dirtyShowFields.current.name)) {
      setSlugManuallyEdited(hasCustomSlug)
    }
    if (!sameShow) {
      setAdvancedOpen(false)
    }
  }, [currentShow, showForm, hasCustomSlug])

  useEffect(() => {
    if (!canReadSeries || !currentShow?.seriesMembership) {
      showForm.setValue('seriesId', undefined)
      setSeriesConflict(null)
    }
  }, [canReadSeries, currentShow?.seriesMembership, showForm])

  const isDirty = showForm.formState.isDirty
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    const currentQuery = new URLSearchParams(currentLocation.search)
    const nextQuery = new URLSearchParams(nextLocation.search)
    currentQuery.delete('section')
    nextQuery.delete('section')
    const changingSection =
      currentLocation.pathname === nextLocation.pathname &&
      currentLocation.hash === nextLocation.hash &&
      currentQuery.toString() === nextQuery.toString()
    return (
      isDirty &&
      !changingSection &&
      !(
        currentShow?.slug &&
        currentLocation.pathname.split('/')[1] !== currentShow.slug &&
        nextLocation.pathname ===
          `/${currentShow.slug}${currentLocation.pathname.slice(currentLocation.pathname.indexOf('/', 1))}` &&
        nextLocation.search === currentLocation.search &&
        nextLocation.hash === currentLocation.hash
      ) &&
      currentLocation.pathname + currentLocation.search !==
        nextLocation.pathname + nextLocation.search
    )
  })
  useEffect(() => {
    if (!isDirty) return
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', beforeUnload)
    return () => window.removeEventListener('beforeunload', beforeUnload)
  }, [isDirty])

  const discardChanges = () => {
    setSeriesConflict(null)
    seriesRevision.current = currentShow?.seriesMembership?.revision
    showForm.reset(getShowSettings(currentShow))
    setSlugManuallyEdited(hasCustomSlug)
  }

  const validation = computeValidationState(staffQuery.data, divisionsQuery.data)

  const watchedName = useWatch({ control: showForm.control, name: 'name' })
  const watchedSlug = useWatch({ control: showForm.control, name: 'slug' })

  useEffect(() => {
    if (slugManuallyEdited || hasCustomSlug) return
    const suggestion = slugify(watchedName ?? '')
    if (suggestion === watchedSlug) return
    showForm.setValue('slug', suggestion, { shouldValidate: true })
  }, [slugManuallyEdited, watchedName, watchedSlug, showForm, hasCustomSlug])

  const slugPreviewPath = watchedSlug ? `/${watchedSlug}/show-classes` : '/<slug>/show-classes'

  const onSubmit = (values: ShowDetailsFormValues) => {
    if (
      !showId ||
      !seriesValid ||
      seriesConflict ||
      !canManageSettings ||
      permissionLoading ||
      updateShow.isPending
    )
      return
    const payload: Record<string, unknown> = {}
    const savedSeriesId = currentShow?.seriesMembership?.series?.id ?? null
    // A membership that became read-only after an edit keeps its saved series.
    if (
      currentShow?.seriesMembership?.canEdit &&
      values.seriesId !== undefined &&
      values.seriesId !== savedSeriesId
    ) {
      payload.seriesId = values.seriesId
      payload.expectedMembershipRevision = seriesRevision.current
    }
    if (values.name !== (currentShow?.name ?? '')) payload.name = values.name.trim()
    if (values.orgId && values.orgId !== currentShow?.orgId) payload.orgId = values.orgId
    const startDateOnly = toDateOnly(values.startDate)
    if (startDateOnly && startDateOnly !== currentShow?.startDate) payload.startDate = startDateOnly
    const endDateOnly = toDateOnly(values.endDate)
    if (endDateOnly && endDateOnly !== currentShow?.endDate) payload.endDate = endDateOnly
    if ((values.location ?? '') !== (currentShow?.location ?? '')) {
      payload.location = values.location?.trim() || null
    }
    if (values.hostState !== (currentShow?.hostState ?? null)) payload.hostState = values.hostState
    if (values.basicDetailsPublished !== (currentShow?.basicDetailsPublished ?? false))
      payload.basicDetailsPublished = values.basicDetailsPublished
    if ((values.rulesVersion ?? '') !== (currentShow?.rulesVersion ?? '')) {
      payload.rulesVersion = values.rulesVersion?.trim() || null
    }
    const currentApplyTiebreakers = resolveApplyTiebreakers(currentShow?.pointsConfig ?? undefined)
    if (values.applyTiebreakers !== currentApplyTiebreakers) {
      payload.applyTiebreakers = values.applyTiebreakers
    }
    if (values.enableClubs !== (currentShow?.enableClubs ?? true)) {
      payload.enableClubs = values.enableClubs
    }
    if (values.labelOnlyMode !== (currentShow?.labelOnlyMode ?? false)) {
      payload.labelOnlyMode = values.labelOnlyMode
    }
    if (values.labelPrintingDisabled !== (currentShow?.labelPrintingDisabled ?? false)) {
      payload.labelPrintingDisabled = values.labelPrintingDisabled
    }
    if (values.staffMayExhibit !== (currentShow?.staffMayExhibit ?? true)) {
      payload.staffMayExhibit = values.staffMayExhibit
    }

    if (values.reserveGrandEnabled !== (currentShow?.reserveGrandEnabled ?? false)) {
      payload.reserveGrandEnabled = values.reserveGrandEnabled
    }
    if (values.defaultPlaceCount !== (currentShow?.defaultPlaceCount ?? DEFAULT_PLACE_COUNT)) {
      payload.defaultPlaceCount = values.defaultPlaceCount
    }

    const currentSettings = getShowSettings(currentShow)
    const awardFlagsChanged = SPECIAL_AWARD_KEYS.some(
      key => values.staffAwardEligibility[key] !== currentSettings.staffAwardEligibility[key]
    )
    if (
      awardFlagsChanged ||
      values.bestInDivisionEnabled !== currentSettings.bestInDivisionEnabled ||
      values.heroAwardEnabled !== currentSettings.heroAwardEnabled ||
      values.clubSpiritAwardEnabled !== currentSettings.clubSpiritAwardEnabled ||
      values.clubChampionAwardEnabled !== currentSettings.clubChampionAwardEnabled
    ) {
      // Merge into the latest policy so unrelated award configuration survives the save.
      payload.awardsPolicy = {
        ...currentShow?.awardsPolicy,
        bestInDivisionEnabled: values.bestInDivisionEnabled,
        heroAwardEnabled: values.heroAwardEnabled,
        clubSpiritAwardEnabled: values.clubSpiritAwardEnabled,
        clubChampionAwardEnabled: values.clubChampionAwardEnabled,
        awardEligibility: {
          ...currentShow?.awardsPolicy?.awardEligibility,
          ...Object.fromEntries(
            SPECIAL_AWARD_KEYS.map(key => [
              key,
              {
                ...currentShow?.awardsPolicy?.awardEligibility?.[key],
                staffEligible: values.staffAwardEligibility[key],
              },
            ])
          ),
        },
      }
    }

    const slugChanged = values.slug !== (currentShow?.slug ?? '')
    if (slugChanged) {
      payload.slug = values.slug
    }
    const requireSlugConfirmation = slugChanged && slugManuallyEdited

    if (Object.keys(payload).length === 0) {
      toast.info('No changes to save')
      return
    }

    if (requireSlugConfirmation) {
      const confirmed = window.confirm(
        'Changing the slug can break previously shared links. Continue with this change?'
      )
      if (!confirmed) {
        return
      }
    }

    showForm.clearErrors('slug')

    updateShow.mutate(
      { showId, payload },
      {
        onSuccess: updated => {
          showForm.reset(getShowSettings(updated))
          setCurrentShow(updated)
          toast.success('Show settings saved')
        },
        onError: async error => {
          const seriesCode =
            error instanceof ApiError &&
            typeof error.payload?.code === 'string' &&
            error.payload.code.startsWith('SERIES_')
          if (seriesCode) {
            setSection('details')
            showForm.setError('seriesId', {
              type: 'manual',
              message: String(error.payload?.message ?? 'Unable to save series membership.'),
            })
            if (error.payload?.code === 'SERIES_MEMBERSHIP_STALE') {
              try {
                await queryClient.cancelQueries({ queryKey: ['show-by-slug'] })
                const { show } = await apiClient.lookupShow({ showId })
                await queryClient.cancelQueries({ queryKey: ['show-by-slug'] })
                for (const [key, cached] of queryClient.getQueriesData<TShow>({
                  queryKey: ['show-by-slug'],
                })) {
                  if (cached?.id === showId) queryClient.setQueryData(key, show)
                }
                if (show.slug)
                  queryClient.setQueryData(['show-by-slug', show.slug, undefined], show)
                setSeriesConflict(show)
                setCurrentShow(show)
              } catch {
                toast.error('Unable to reload saved membership. Retry saving when connected.')
              }
            }
          }
          if (!seriesCode && error.message.includes('[409]') && slugChanged) {
            setSection('details')
            setAdvancedOpen(true)
            showForm.setError('slug', {
              type: 'manual',
              message: 'Slug is already in use by another show.',
            })
          }
          toast.error(error.message)
        },
      }
    )
  }

  const handleAddStaff = async (role: StaffRole, payload: StaffInput) => {
    if (!showId || permissionLoading || !canManageStaff) return false
    try {
      await addStaff.mutateAsync({ showId, payload: { role, ...payload } })
      toast.success('Staff assigned')
      return true
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to add staff')
      return false
    }
  }

  const handleRemoveStaff = async (assignmentId: string) => {
    if (!showId || permissionLoading || !canManageStaff) return false
    try {
      await removeStaff.mutateAsync({ showId, assignmentId })
      toast.success('Staff assignment removed')
      return true
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to remove staff')
      return false
    }
  }

  if (!currentShow || !showId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Select a show to configure</CardTitle>
          <CardDescription>Choose a show or create a new one from the Shows page.</CardDescription>
        </CardHeader>
        <CardFooter>
          <Button asChild variant="outline">
            <Link to="/shows">Go to Shows</Link>
          </Button>
        </CardFooter>
      </Card>
    )
  }

  const configurationSection = ['details', 'participation', 'awards'].includes(section)
  const settingsDisabled = updateShow.isPending || permissionLoading || !canManageSettings
  const onInvalid = () => {
    setSection('details')
    if (showForm.getFieldState('slug').invalid) setAdvancedOpen(true)
    toast.error('Review the highlighted show details before saving.')
  }

  return (
    <div className="space-y-6">
      <header className="space-y-3 border-b pb-4 sm:pb-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-2">
            <PageHeading>Show Setup</PageHeading>
            {user.data?.isAdmin && currentShow.id && (
              <Button asChild variant="outline">
                <Link to={`/admin/show-transfer?showId=${encodeURIComponent(currentShow.id)}`}>
                  Import & export
                </Link>
              </Button>
            )}
            <p className="min-w-0 break-words font-medium">
              {currentShow.name}{' '}
              <Badge variant="secondary" className="ml-1 align-middle">
                {STATUS_LABELS[currentShow.status]}
              </Badge>
            </p>
          </div>
          <Button asChild variant="outline" className="shrink-0 self-start">
            <Link to={`/${currentShow.slug}/run`}>Open Show Dashboard</Link>
          </Button>
        </div>
        <div
          className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground"
          aria-label="Setup checks"
        >
          <button
            type="button"
            className="text-left underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
            onClick={() => setSection('classes')}
          >
            {divisionsQuery.isError
              ? 'Class configuration unavailable'
              : divisionsQuery.isLoading
                ? 'Checking classes…'
                : validation.hasClasses
                  ? `${validation.totalClasses} classes configured`
                  : 'Classes need configuration'}
          </button>
          <button
            type="button"
            className="text-left underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
            onClick={() => setSection('staff')}
          >
            {staffQuery.isError
              ? 'Staff coverage unavailable'
              : permissionLoading
                ? 'Checking staff coverage…'
                : validation.hasJudges && validation.hasLeadSecretaries
                  ? 'Judge and lead secretary coverage complete'
                  : 'Assign a judge and lead secretary'}
          </button>
        </div>
      </header>

      {!permissionLoading && !canManageSettings && (
        <p className="text-sm text-muted-foreground">
          You can view these settings. Updating them requires{' '}
          {formatRoleList(permissions.getAllowedRoles('show:write'))}.
        </p>
      )}

      <Tabs
        value={section}
        onValueChange={setSection}
        orientation="vertical"
        className="grid gap-6 lg:grid-cols-[12rem_minmax(0,1fr)]"
      >
        <div className="lg:hidden space-y-2">
          <Label htmlFor="setup-section">Section</Label>
          <Select value={section} onValueChange={setSection}>
            <SelectTrigger id="setup-section">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SETUP_SECTIONS.map(item => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <TabsList
          aria-label="Show setup sections"
          className="hidden h-auto w-full flex-col items-stretch justify-start gap-1 bg-transparent p-0 lg:flex lg:sticky lg:top-6 lg:self-start"
        >
          {SETUP_SECTIONS.map(item => (
            <TabsTrigger
              key={item.value}
              value={item.value}
              className="h-auto min-h-11 flex-none justify-start whitespace-normal px-3 py-3 text-left data-[state=active]:bg-muted data-[state=active]:shadow-none"
            >
              {item.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <div className="min-w-0 space-y-6">
          <Form {...showForm}>
            <form
              id="show-settings-form"
              data-testid="show-details-form"
              onSubmit={showForm.handleSubmit(onSubmit, onInvalid)}
            >
              <fieldset disabled={settingsDisabled} className="min-w-0">
                <TabsContent
                  value="details"
                  forceMount
                  className="data-[state=inactive]:hidden space-y-6"
                >
                  <SectionHeading
                    title="Show details"
                    description="Event information and connections to other editions."
                  />
                  <FormField
                    control={showForm.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Show name</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Honey Show name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={showForm.control}
                    name="orgId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Organization</FormLabel>
                        <Select
                          value={field.value ?? ''}
                          onValueChange={value => field.onChange(value)}
                          disabled={
                            settingsDisabled ||
                            Boolean(currentShow?.seriesMembership?.series) ||
                            organizationsQuery.isLoading ||
                            organizationOptions.length === 0
                          }
                        >
                          <FormControl>
                            <SelectTrigger aria-label="Organization">
                              <SelectValue placeholder={organizationPlaceholder} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {organizationOptions.map(option => (
                              <SelectItem key={option.id} value={option.id}>
                                {option.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {user.data?.isAdmin && (
                          <FormDescription>
                            Manage organizations on the{' '}
                            <Link to="/organizations" className="underline">
                              Organizations page
                            </Link>
                            .
                          </FormDescription>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      control={showForm.control}
                      name="startDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Start date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={showForm.control}
                      name="endDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>End date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={showForm.control}
                    name="location"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Location</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Venue or hall" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={showForm.control}
                    name="hostState"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Host state</FormLabel>
                        <FormControl>
                          <HostStateSelect
                            value={field.value}
                            onChange={field.onChange}
                            disabled={settingsDisabled}
                          />
                        </FormControl>
                        <FormMessage />
                        <p className="text-sm text-muted-foreground">
                          Optional. Included in public statistics when results are released.
                        </p>
                      </FormItem>
                    )}
                  />
                  <div className="space-y-2">
                    <SettingCheckbox
                      name="basicDetailsPublished"
                      label="Publish show details"
                      description="Publish the show's name, dates and location in the public directory. This does not open entries or release results. Released results remain public until withdrawn separately in Reports."
                    />
                    {currentShow?.basicDetailsPublished && (
                      <Link
                        to={`/public/shows/${encodeURIComponent(currentShow.slug)}`}
                        className="text-sm underline underline-offset-4"
                      >
                        View public show page
                      </Link>
                    )}
                  </div>
                  {permissions.can('reports:results') && currentShow?.seriesMembership && (
                    <div className="space-y-2">
                      <SeriesField
                        organizationId={showForm.watch('orgId')}
                        value={showForm.watch('seriesId')}
                        retainedSeries={currentShow.seriesMembership.series}
                        onChange={value =>
                          showForm.setValue('seriesId', value, { shouldDirty: value !== undefined })
                        }
                        disabled={
                          updateShow.isPending ||
                          !canManageSettings ||
                          !currentShow.seriesMembership.canEdit
                        }
                        onValidityChange={setSeriesValid}
                        showEditions
                      />
                      {currentShow.seriesMembership.series && (
                        <Link
                          className="underline"
                          to={`/show-series/${currentShow.seriesMembership.series.id}/settings`}
                        >
                          Series settings
                        </Link>
                      )}
                      {currentShow.seriesMembership.series && (
                        <p className="text-sm text-muted-foreground">
                          To change organizations, first select None and save show settings.
                        </p>
                      )}
                      {showForm.formState.errors.seriesId && (
                        <p role="alert">{showForm.formState.errors.seriesId.message}</p>
                      )}
                      <DerivedPreviousEdition
                        show={currentShow}
                        pending={
                          (showForm.watch('seriesId') !== undefined &&
                            showForm.watch('seriesId') !==
                              (currentShow.seriesMembership.series?.id ?? null)) ||
                          showForm.watch('startDate') !== formatDateInput(currentShow.startDate)
                        }
                      />
                      {seriesConflict && (
                        <div role="alert" className="space-y-2">
                          <p>
                            Saved membership changed to{' '}
                            {seriesConflict.seriesMembership?.series?.name ?? 'None'}. Your draft is
                            retained. Review your selection before saving again.
                          </p>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              seriesRevision.current = seriesConflict.seriesMembership?.revision
                              setSeriesConflict(null)
                              showForm.clearErrors('seriesId')
                            }}
                          >
                            I have reviewed the saved membership
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  <FormField
                    control={showForm.control}
                    name="rulesVersion"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Rules reference</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="e.g., 2025-v1" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <details
                    open={advancedOpen}
                    onToggle={event => setAdvancedOpen(event.currentTarget.open)}
                    className="rounded-md border p-4"
                  >
                    <summary className="cursor-pointer text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                      Advanced · Show URL
                    </summary>
                    <div className="pt-4">
                      <FormField
                        control={showForm.control}
                        name="slug"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Show URL</FormLabel>
                            <FormControl>
                              <Input
                                value={field.value}
                                onChange={event => {
                                  const raw = event.target.value
                                  const normalized = raw
                                    .toLowerCase()
                                    .replace(/\s+/g, '-')
                                    .replace(/[^a-z0-9-]/g, '')
                                    .slice(0, SLUG_MAX_LENGTH)
                                  setSlugManuallyEdited(true)
                                  showForm.clearErrors('slug')
                                  field.onChange(normalized)
                                }}
                                onBlur={field.onBlur}
                                onFocus={() => setSlugManuallyEdited(true)}
                                ref={field.ref}
                                maxLength={SLUG_MAX_LENGTH}
                                autoComplete="off"
                                spellCheck={false}
                                placeholder="e.g., harvest-fair"
                                data-testid="show-slug-input"
                              />
                            </FormControl>
                            <FormDescription>
                              <span>Shareable URL segment. Preview: </span>
                              <code className="break-all">{slugPreviewPath}</code>
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </details>
                </TabsContent>
                <TabsContent
                  value="participation"
                  forceMount
                  className="data-[state=inactive]:hidden space-y-6"
                >
                  <SectionHeading
                    title="Participation"
                    description="Choose who can exhibit and how entries and labels are handled."
                  />
                  <SettingCheckbox
                    name="staffMayExhibit"
                    label="Show staff may exhibit"
                    description="Allow show staff to enter their own exhibits. When off, new staff exhibits are blocked and existing exhibits are preserved. Their permissions to manage other entrants stay the same."
                  />
                  <SettingCheckbox
                    name="enableClubs"
                    label="Use clubs for this show"
                    description="Let entrants provide a club affiliation and include club sections in show reports."
                  />
                  <Separator />
                  <h3 className="text-base font-semibold">Exhibit labels</h3>
                  <SettingCheckbox
                    name="labelPrintingDisabled"
                    inverted
                    label="Print exhibit labels"
                    description="When off, staff create Exhibit ID labels manually."
                  />
                  <details className="rounded-md border p-4">
                    <summary className="cursor-pointer text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                      Advanced · Label-Only Mode
                    </summary>
                    <div className="pt-4">
                      <SettingCheckbox
                        name="labelOnlyMode"
                        label="Enable Label-Only Mode"
                        description="Use the app for labels and staffing only. This hides entry tracking, Check-In, judging, awards, and results tools."
                      />
                    </div>
                  </details>
                </TabsContent>
                <TabsContent
                  value="awards"
                  forceMount
                  className="data-[state=inactive]:hidden space-y-6"
                >
                  <SectionHeading
                    title="Awards & judging"
                    description="Configure special awards, places per class, and tie-breaking."
                  />
                  <div className="space-y-4">
                    <SettingCheckbox name="bestInDivisionEnabled" label="Best in Division" />
                    <SettingCheckbox name="reserveGrandEnabled" label="Reserve Grand Champion" />
                    <SettingCheckbox
                      name="heroAwardEnabled"
                      label="Hero Award"
                      description="Veteran and first responder eligibility is confirmed in Check-In."
                    />
                    {showForm.watch('enableClubs') && (
                      <>
                        <SettingCheckbox
                          name="clubSpiritAwardEnabled"
                          label="Club Spirit Award"
                          description="Goes to the club with the most entries."
                        />
                        <SettingCheckbox
                          name="clubChampionAwardEnabled"
                          label="Club Champion Award"
                          description="Goes to the club with the most points."
                        />
                      </>
                    )}
                  </div>
                  <Separator />
                  <FormField
                    control={showForm.control}
                    name="defaultPlaceCount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Places per class</FormLabel>
                        <Select
                          value={String(field.value)}
                          onValueChange={value => field.onChange(Number(value))}
                          disabled={settingsDisabled}
                        >
                          <FormControl>
                            <SelectTrigger ref={field.ref} onBlur={field.onBlur}>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {SHOW_PLACE_COUNTS.map(count => (
                              <SelectItem key={count} value={String(count)}>
                                {formatPlaceRange(count)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Judging shows this many placements for each class. A class can set its own
                          number on Show Classes.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={showForm.control}
                    name="applyTiebreakers"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tie-breaking</FormLabel>
                        <Select
                          value={field.value ? 'apply' : 'allow'}
                          onValueChange={value => field.onChange(value === 'apply')}
                          disabled={settingsDisabled}
                        >
                          <FormControl>
                            <SelectTrigger ref={field.ref} onBlur={field.onBlur}>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="apply">Apply tie-breaking</SelectItem>
                            <SelectItem value="allow">Allow ties</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Break leaderboard ties by placement counts or leave them as shared ranks.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {showForm.watch('staffMayExhibit') && (
                    <>
                      <Separator />
                      <div className="space-y-4">
                        <div className="space-y-1">
                          <h3 className="text-base font-semibold">Staff award eligibility</h3>
                          <p className="text-sm text-muted-foreground">
                            Choose which special awards exhibiting staff may win. Staff can still
                            earn points when an award is unchecked.
                          </p>
                        </div>
                        {SPECIAL_AWARD_KEYS.map(key => (
                          <SettingCheckbox
                            key={key}
                            name={`staffAwardEligibility.${key}`}
                            label={SPECIAL_AWARD_LABELS[key]}
                            ariaLabel={`${SPECIAL_AWARD_LABELS[key]} staff eligibility`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </TabsContent>
              </fieldset>
            </form>
          </Form>

          <TabsContent
            value="classes"
            forceMount
            className="space-y-6 data-[state=inactive]:hidden"
          >
            <SectionHeading
              title="Classes"
              description="Review the divisions and classes configured for this show."
            />
            <div className="flex flex-wrap gap-3">
              <Button asChild variant="outline">
                <Link to={`/${currentShow.slug}/show-classes`}>Manage classes</Link>
              </Button>
              {currentShow.seriesMembership?.series && canReadSeries && (
                <Button asChild variant="outline">
                  <Link to={`/show-series/${currentShow.seriesMembership.series.id}/alignment`}>
                    Match classes across years
                  </Link>
                </Button>
              )}
            </div>
            {divisionsQuery.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : divisionsQuery.isError ? (
              <div role="alert" className="space-y-2">
                <p>Unable to load classes.</p>
                <Button variant="outline" onClick={() => void divisionsQuery.refetch()}>
                  Retry
                </Button>
              </div>
            ) : divisionsQuery.data?.length ? (
              <ul className="divide-y border-y">
                {divisionsQuery.data.map(division => (
                  <li
                    key={division.id ?? division.code}
                    className="flex items-start justify-between gap-4 py-4"
                  >
                    <div className="min-w-0">
                      <p className="font-medium break-words">{division.name}</p>
                      <p className="text-sm text-muted-foreground">{division.code}</p>
                    </div>
                    <Badge variant="secondary" className="shrink-0">
                      {division.classes.length} classes
                    </Badge>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground">
                No divisions configured yet. Add classes to get started.
              </p>
            )}
          </TabsContent>
          <TabsContent value="staff" className="space-y-6">
            <ShowStaffRoster
              showId={currentShow.id ?? ''}
              key={showId}
              assignments={staffQuery.data ?? []}
              canManage={canManageStaff}
              permissionNotice={staffPermissionNotice}
              isLoading={permissionLoading}
              isError={staffQuery.isError}
              onRetry={() => void staffQuery.refetch()}
              isAdding={addStaff.isPending}
              isRemoving={removeStaff.isPending}
              onAdd={handleAddStaff}
              onRemove={handleRemoveStaff}
              onInvite={user.data?.isAdmin ? setInviting : undefined}
            />
          </TabsContent>

          {(configurationSection || isDirty) && canManageSettings && (
            <div
              className="sticky -bottom-6 z-10 flex flex-wrap items-center justify-between gap-3 border-t bg-background py-4"
              aria-label="Save show settings"
            >
              <p className="text-sm text-muted-foreground" role="status">
                {updateShow.isPending
                  ? 'Saving settings…'
                  : isDirty
                    ? 'Unsaved changes'
                    : 'No unsaved changes'}
              </p>
              <div className="flex w-full gap-2 sm:w-auto">
                <Button
                  type="button"
                  variant="outline"
                  onClick={discardChanges}
                  disabled={!isDirty || updateShow.isPending}
                  className="flex-1 sm:flex-none"
                >
                  Discard
                </Button>
                <Button
                  type="submit"
                  form="show-settings-form"
                  disabled={!isDirty || settingsDisabled || !seriesValid || Boolean(seriesConflict)}
                  className="flex-1 sm:flex-none"
                >
                  {updateShow.isPending ? 'Saving…' : 'Save show settings'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Tabs>
      {/* Outside the tabs, so changing section never unmounts an invite and its one-time link. */}
      {inviting ? <InviteUserDialog person={inviting} onClose={() => setInviting(null)} /> : null}
      <Dialog
        open={blocker.state === 'blocked'}
        onOpenChange={open => {
          if (!open && !updateShow.isPending && blocker.state === 'blocked') blocker.reset()
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leave with unsaved changes?</DialogTitle>
            <DialogDescription>
              Your changes have not been saved. Keep editing or discard the changes and leave.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              disabled={updateShow.isPending}
              onClick={() => blocker.state === 'blocked' && blocker.reset()}
            >
              Keep editing
            </Button>
            <Button
              disabled={updateShow.isPending}
              onClick={() => {
                if (blocker.state === 'blocked') {
                  discardChanges()
                  blocker.proceed()
                }
              }}
            >
              Discard and leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
